import { useEffect, useRef, useState } from 'react';

/**
 * useIdleLogout: triggers a warning after `warnAfterMs` of inactivity, then logs out after `warnMs` more.
 * Any user activity (mouse/keyboard/touch) resets the timer.
 */
export function useIdleLogout({ warnAfterMs = 30 * 60 * 1000, warnMs = 60 * 1000, onLogout, enabled = true }) {
  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const warnTimer = useRef(null);
  const logoutTimer = useRef(null);
  const countdownTimer = useRef(null);

  const clearAll = () => {
    if (warnTimer.current) clearTimeout(warnTimer.current);
    if (logoutTimer.current) clearTimeout(logoutTimer.current);
    if (countdownTimer.current) clearInterval(countdownTimer.current);
  };

  const start = () => {
    if (!enabled) return;
    clearAll();
    setWarning(false);
    warnTimer.current = setTimeout(() => {
      setWarning(true);
      setSecondsLeft(Math.floor(warnMs / 1000));
      countdownTimer.current = setInterval(() => {
        setSecondsLeft(s => (s > 0 ? s - 1 : 0));
      }, 1000);
      logoutTimer.current = setTimeout(() => {
        clearAll();
        onLogout && onLogout();
      }, warnMs);
    }, warnAfterMs);
  };

  const stay = () => start();

  useEffect(() => {
    if (!enabled) return;
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    const onActivity = () => { if (!warning) start(); };
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    start();
    return () => {
      clearAll();
      events.forEach(e => window.removeEventListener(e, onActivity));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, warning]);

  return { warning, secondsLeft, stay };
}
